import './style.css'
import {
  Refresh,
  SearchTwoTone as SearchTwoToneIcon,
} from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { type FormEvent, useRef } from 'react'
import {
  GaForm,
  type GaFormHandle,
  type GaFormProps,
} from '@/features/analytics/gaForm/GaForm'
import { useDeferredGaSubmit } from '@/features/analytics/gaForm/useDeferredGaSubmit'
import { useSearch } from '@/features/search/hooks/useSearch'
import { useAppSelector } from '@/redux/hooks'
import {
  buildSearchGaFormProps,
  getFormFieldCausingError,
  getFormStatus,
} from '../../utils'
import { SearchFilterPopover } from './SearchFilterPopover'
import { SearchTextField } from './SearchTextField'

const baseGaFormProps: Omit<GaFormProps, 'children'> = {
  formName: 'search',
  formId: 'search',
  formLocation: 'main',
  formDestination: 'emc/search',
  formSubmitText: 'searchicon',
}

export const SearchInput = () => {
  const gaFormRef = useRef<GaFormHandle>(null)
  const { applyNextSearch, searchResults, selectors, updateNext } = useSearch()
  const { details, request } = searchResults
  const {
    isLoading = false,
    isSuccess = false,
    isError = false,
  } = request ?? {}
  const payload = useAppSelector(selectors.selectSearchPayload)
  const totalCount = details.totalCount ?? 0
  const formStatus = getFormStatus(isError, totalCount)
  const formFieldCausingError = getFormFieldCausingError(isSuccess, totalCount)

  const { markPending } = useDeferredGaSubmit(gaFormRef, request)

  const handleSubmit = (_event?: FormEvent<HTMLFormElement>) => {
    const searchTextField = _event
      ? new FormData(_event.currentTarget).get('searchText')
      : null

    if (typeof searchTextField === 'string') {
      // Keep submit deterministic even if SearchTextField debounce is still pending.
      updateNext({ searchText: searchTextField })
    }

    markPending()
    applyNextSearch()
  }

  const gaFormProps: Omit<GaFormProps, 'children'> = buildSearchGaFormProps({
    baseGaFormProps,
    onSubmit: handleSubmit,
    formStatus: formStatus,
    formFieldCausingError: formFieldCausingError,
    payload,
    totalCount,
  })

  return (
    <GaForm ref={gaFormRef} {...gaFormProps}>
      <Stack direction="row">
        <SearchTextField />
        <Button
          name="search"
          variant="contained"
          size="large"
          type="submit"
          sx={{
            height: 56,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
        >
          {isLoading ? (
            <Refresh
              sx={{ animationName: 'loading-icon', animationDuration: '1s' }}
            />
          ) : (
            <SearchTwoToneIcon />
          )}
        </Button>
        <SearchFilterPopover />
      </Stack>
    </GaForm>
  )
}
